import {useState} from 'react'
import {dialogApi} from '../../../../api/dialogApi'
import {BaseModal} from '../../../../components/BaseModal/BaseModal'
import styles from './DialogViolations.module.scss'
import {FlaggedResponseCard} from './FlaggedResponseCard/FlaggedResponseCard'
import {DIALOG_AVATAR_IMAGE_URL} from '../../../../constants/dialogConstants'
import {getHexFromColorText} from '../../../../utils'
import {
  useWuShowToast,
  WuIcon,
  WuSegmentedSelector,
  WuSegmentedSelectorItem,
} from '@npm-questionpro/wick-ui-lib'
import {useParams} from 'react-router'
import {useWt} from '@npm-questionpro/wick-ui-lib'
import {DIALOG} from '../../../../Translation/English/dialog'
import type {IRestrictedUser} from '../../../../types'

interface IDialogViolationsProps {
  showViolations: boolean
  setShowViolations: (value: boolean) => void
}

export const DialogViolations: React.FC<IDialogViolationsProps> = ({
  showViolations,
  setShowViolations,
}) => {
  const wt = useWt()
  const {showToast} = useWuShowToast()
  const updateAvatarStatusMutation = dialogApi.useUpdateDialogUserAvatarStatus()
  const {dialogId} = useParams<{dialogId: string}>()

  const [selectedOption, setSelectedOption] = useState('review')
  const {data: flaggedResponses} = dialogApi.useFlaggedResponses(dialogId ?? '')
  const {data: dialogDetails} = dialogApi.useDialogDetails(dialogId ?? '')

  const getRadioLabel = (label: string, count: number): React.ReactElement => {
    return (
      <>
        <div className={styles.dialogViolationsLabelText}>{wt(label)}</div>
        <div className={styles.dialogViolationsLabelCount}>{count}</div>
      </>
    )
  }

  const flaggedCount =
    [
      ...(flaggedResponses?.responses || []),
      ...(flaggedResponses?.replies || []),
    ].length || 0

  const radioOptions = [
    {value: 'review', label: getRadioLabel(DIALOG.pendingReview, flaggedCount)},
    {
      value: 'people',
      label: getRadioLabel(
        DIALOG.restrictedParticipants,
        dialogDetails?.restrictedUsers?.length || 0,
      ),
    },
  ]

  const allowUser = async (user: {avatarId: string}): Promise<void> => {
    try {
      await updateAvatarStatusMutation.mutateAsync({
        id: user.avatarId,
        isRestricted: false,
        dialogId,
      })
    } catch {
      showToast({message: wt(DIALOG.failedToAllowUser), variant: 'error'})
    }
  }

  return (
    <BaseModal
      visible={showViolations}
      headerTitle={wt(DIALOG.communityViolations)}
      onCloseModal={() => setShowViolations(false)}
      maxWidth="800px"
      variant="critical"
    >
      <div className={styles.dialogViolations}>
        <div className={styles.dialogViolationsRadiogroup}>
          <WuSegmentedSelector
            value={selectedOption}
            onChange={setSelectedOption}
          >
            {radioOptions.map(option => (
              <WuSegmentedSelectorItem
                key={option.value}
                value={option.value}
                className={styles.dialogViolationsLabel}
              >
                {option.label}
              </WuSegmentedSelectorItem>
            ))}
          </WuSegmentedSelector>
        </div>
        <div className={styles.dialogViolationsContent}>
          {selectedOption === 'review' && (
            <>
              {flaggedResponses?.responses?.map((response, index) => (
                <FlaggedResponseCard
                  key={response.id ?? index}
                  data={response}
                  type="response"
                />
              ))}
              {flaggedResponses?.replies?.map((reply, index) => (
                <FlaggedResponseCard
                  key={reply.id ?? index}
                  data={reply}
                  type="reply"
                />
              ))}
            </>
          )}
          {selectedOption === 'review' && flaggedCount < 1 && (
            <div className="no-data-content">{wt(DIALOG.noViolations)}</div>
          )}
          {selectedOption === 'people' && (
            <div>
              {(dialogDetails?.restrictedUsers?.length ?? 0) > 0 &&
                dialogDetails?.restrictedUsers?.map(
                  (user: IRestrictedUser, index: number) => (
                    <div className={styles.restrictedUsers} key={index}>
                      <div className={styles.restrictedUsersDetails}>
                        <div className={styles.restrictedUsersAvatar}>
                          <img
                            src={`${DIALOG_AVATAR_IMAGE_URL}${user.username}`}
                            alt="coverImage"
                            width={'100%'}
                            height={'100%'}
                            loading="lazy"
                            className={styles.restrictedUsersAvatarImg}
                            style={{
                              backgroundColor: getHexFromColorText(
                                user?.username?.split(' ')[0],
                              ),
                            }}
                          />
                        </div>
                        <div className={styles.restrictedUsersUsername}>
                          {user.username}
                        </div>
                      </div>
                      <WuIcon
                        icon="wm-person-check"
                        className={styles.restrictedUsersBtn}
                        onClick={() => allowUser(user)}
                      />
                    </div>
                  ),
                )}
            </div>
          )}
          {selectedOption === 'people' &&
            (dialogDetails?.restrictedUsers?.length ?? 0) < 1 && (
              <div className="no-data-content">
                {wt(DIALOG.noRestrictedParticipants)}
              </div>
            )}
        </div>
      </div>
    </BaseModal>
  )
}
